import { useState } from 'react'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import Icon from '@/components/Icon/Icon'
import clsx from 'clsx'
import { AsteriskIcon, CheckIcon } from 'lucide-react'

export type SelectOption<Id extends number | string = string | number> = {
  id: Id
  name: string
}

export type SelectProps<Id extends number | string = string | number> = {
  options: SelectOption<Id>[]
  label?: string
  selectedOptionId?: Id
  onChange?: (optionId: Id) => void
  className?: string
  required?: boolean
}

export default function Select<Id extends number | string = string | number>({
  options,
  label = '',
  selectedOptionId,
  onChange,
  className,
  required = false,
}: SelectProps<Id>) {
  const [internalId, setInternalId] = useState<number | string | null>(
    selectedOptionId ?? null,
  )

  const currentId = selectedOptionId ?? internalId
  const selectedOption = options.find((o) => o.id === currentId) ?? null
  const handleSelect = (option: SelectOption<Id>) => {
    setInternalId(option.id)
    onChange?.(option.id)
  }
  return (
    <Listbox value={selectedOption} onChange={handleSelect}>
      <>
        <ListboxButton
          className={clsx(
            'form-field-root flex flex-row gap-1 last:rounded-b-md rounded-b-none relative w-full max-w-full cursor-pointer',
          )}
        >
          <div className="flex flex-col gap-1 grow-1 min-w-0 w-full [&>*]:cursor-pointer">
            <label className="text-themed-grey-500 w-full text-left font-bold bg-transparent text-xs">
              {label}
              {required && (
                <AsteriskIcon className="text-themed-error" size={12} />
              )}
            </label>
            <input
              required={required}
              contentEditable={false}
              readOnly
              value={selectedOption ? selectedOption.name : ''}
              className="w-full text-left outline-none border-none text-sm text-themed-grey-500 font-medium p-0 truncate"
            />
          </div>
          <div className="pointer-events-none flex items-center h-full">
            <Icon name="ChevronDown" />
          </div>
        </ListboxButton>
        <ListboxOptions className="absolute border group border-solid border-themed-grey-200 rounded-b-lg bg-themed-white p-1 -mt-0.5 w-full z-10 max-h-52 overflow-auto scroll-pb-1">
          {options.map((option) => (
            <ListboxOption
              key={option.id}
              value={option}
              onClick={() => handleSelect(option)}
              className={({ selected, focus }) =>
                clsx(
                  selected && !focus
                    ? 'bg-themed-grey-100 group-hover:bg-themed-white'
                    : focus
                    ? 'bg-themed-grey-100'
                    : '',
                  'relative cursor-pointer rounded-lg flex flex-row gap-2 py-2 px-3',
                )
              }
            >
              {({ selected }) => (
                <>
                  <div className="w-4 flex items-center justify-center">
                    {selected && (
                      <CheckIcon
                        size={16}
                        className="size-4 visible text-themed-green-500"
                      />
                    )}
                  </div>
                  <span
                    className={clsx('text-sm/6 text-themed-grey-500 grow text')}
                  >
                    {option.name}
                  </span>
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </>
    </Listbox>
  )
}
